// Tiny async key/value JSON cache for offline-resilient reads (offline-resilience substrate).
//
// Stores last-known-good query payloads so reads survive a cold offline start. NON-confidential by
// design: this is for cacheable view data (market quotes, sparklines, triage counts, …), NOT secrets
// — confidential data belongs in @/lib/localConfidentialStore (encrypted-at-rest + TTL). Keep secrets
// out of here.
//
// Backend: @capacitor/preferences on native (dynamic-imported + try/catch so the web bundle never
// hard-depends on it), localStorage on web. EVERYTHING fails soft: any backend error resolves to
// null/undefined — the cache going wrong must never throw into a render path.
//
// A synchronous in-memory mirror (Map) is written through on every set and hydrated lazily/at startup
// via hydrateCache(); getCachedSync() reads ONLY that mirror so it can feed React Query placeholderData
// (which must be synchronous).

const PREFIX = 'cbam.cache.';

export interface CachedEnvelope<T> {
  data: T;
  /** ISO-8601 timestamp of when this value was written. */
  cachedAt: string;
}

// Synchronous mirror: namespaced key (sans PREFIX) -> serialized envelope string.
// We store the raw JSON string (not the parsed object) so getCachedSync hands back a fresh parse each
// call and callers can't mutate shared cache state.
const memory = new Map<string, string>();

// ---------------------------------------------------------------------------
// Backend (native Preferences / web localStorage) — never throws
// ---------------------------------------------------------------------------

async function isNativeSafe(): Promise<boolean> {
  try {
    const { isNative } = await import('@/lib/native/platform');
    return isNative();
  } catch {
    return false;
  }
}

async function backendSet(fullKey: string, value: string): Promise<void> {
  try {
    if (await isNativeSafe()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key: fullKey, value });
      return;
    }
    if (typeof localStorage !== 'undefined') localStorage.setItem(fullKey, value);
  } catch {
    /* persistence failed: the in-memory mirror still holds the value for this session */
  }
}

async function backendGet(fullKey: string): Promise<string | null> {
  try {
    if (await isNativeSafe()) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key: fullKey });
      return value ?? null;
    }
    return typeof localStorage !== 'undefined' ? localStorage.getItem(fullKey) : null;
  } catch {
    return null;
  }
}

async function backendRemove(fullKey: string): Promise<void> {
  try {
    if (await isNativeSafe()) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.remove({ key: fullKey });
      return;
    }
    if (typeof localStorage !== 'undefined') localStorage.removeItem(fullKey);
  } catch {
    /* ignore */
  }
}

async function backendKeys(): Promise<string[]> {
  try {
    if (await isNativeSafe()) {
      const { Preferences } = await import('@capacitor/preferences');
      const { keys } = await Preferences.keys();
      return keys;
    }
    if (typeof localStorage === 'undefined') return [];
    const out: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) out.push(k);
    }
    return out;
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Store `data` under `key` with an ISO `cachedAt` timestamp. Write-through to the sync mirror. */
export async function setCached<T>(key: string, data: T): Promise<void> {
  const envelope: CachedEnvelope<T> = { data, cachedAt: new Date().toISOString() };
  let serialized: string;
  try {
    serialized = JSON.stringify(envelope);
  } catch {
    // Non-serializable payload: skip caching rather than throw.
    return;
  }
  memory.set(key, serialized);
  await backendSet(PREFIX + key, serialized);
}

/** Async read (persistent backend, falling back to mirror). Null if missing/unparseable. */
export async function getCached<T>(key: string): Promise<CachedEnvelope<T> | null> {
  let raw = await backendGet(PREFIX + key);
  if (raw == null) raw = memory.get(key) ?? null;
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw) as CachedEnvelope<T>;
    // Warm the mirror so subsequent sync reads hit.
    memory.set(key, raw);
    return parsed;
  } catch {
    return null;
  }
}

/** Synchronous read from the in-memory mirror ONLY (for React Query placeholderData). */
export function getCachedSync<T>(key: string): CachedEnvelope<T> | null {
  const raw = memory.get(key);
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as CachedEnvelope<T>;
  } catch {
    return null;
  }
}

/** Remove a cached value from both the mirror and the persistent backend. */
export async function removeCached(key: string): Promise<void> {
  memory.delete(key);
  await backendRemove(PREFIX + key);
}

/**
 * Pre-warm the in-memory mirror from the persistent backend so getCachedSync() hits before first
 * paint. Pass specific `keys` to hydrate just those; omit to hydrate every cbam.cache.* entry.
 * Never throws — on any backend error it resolves having warmed whatever it could.
 */
export async function hydrateCache(keys?: string[]): Promise<void> {
  try {
    if (keys && keys.length) {
      await Promise.all(
        keys.map(async (k) => {
          const raw = await backendGet(PREFIX + k);
          if (raw != null) memory.set(k, raw);
        }),
      );
      return;
    }
    const all = await backendKeys();
    await Promise.all(
      all
        .filter((fk) => fk.startsWith(PREFIX))
        .map(async (fk) => {
          const raw = await backendGet(fk);
          if (raw != null) memory.set(fk.slice(PREFIX.length), raw);
        }),
    );
  } catch {
    /* best-effort warm-up; ignore */
  }
}
